import { HiHeart, HiShieldCheck, HiSparkles, HiChat, HiUserGroup, HiExternalLink } from 'react-icons/hi';
import Section, { SectionHeader } from '../components/ui/Section';
import HeroSection from '../components/ui/HeroSection';
import ChalkboardBackground from '../components/ui/ChalkboardBackground';
import Card, { CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import siteContent from '../data/siteContent.json';
import team from '../data/team.json';
import gabPhoto from '../assets/images/gab_profile_pic_new.png';
import ashleyPhoto from '../assets/images/team/ashley_new.jpg';
import homeBg from '../assets/images/bb-background.png';
import teamBg from '../assets/images/team-banner.png';
import Seo from '../components/Seo';

const philosophyIcons = {
  0: HiSparkles,
  1: HiHeart,
  2: HiUserGroup,
  3: HiShieldCheck,
  4: HiChat,
};

const teamImages = {
  gabrielle: gabPhoto,
  assistant: ashleyPhoto,
};

export default function About() {
  return (
    <>
      <Seo
        title="Meet Gabrielle — Bridal Hair & Makeup Artist"
        description="Meet Gabrielle McKinzie, the licensed and insured artist behind Blush & Bobby. Bridal hair and makeup for Kansas City weddings since 2023."
        path="/about"
      />
      {/* Hero */}
      <HeroSection title="Meet the Team" backgroundImage={teamBg} opacity={0.7} overlayColor="bg-dusty-rose/25" />

      {/* Team */}
      <Section background="white">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {team.members.map((member) => (
            <Card key={member.id} className="h-full flex flex-col rounded-2xl bg-black/70">
              {teamImages[member.id] ? (
                <div className="aspect-square overflow-hidden bg-black/70">
                  <img
                    src={teamImages[member.id]}
                    alt={member.name}
                    className={`w-full h-full object-cover ${member.id === 'gabrielle' ? 'scale-150 object-[center_65%] origin-top' : ''}`}
                  />
                </div>
              ) : (
                <div className="aspect-square flex items-center justify-center bg-black/70">
                  <span className="text-white/50 text-sm">Photo Coming Soon</span>
                </div>
              )}
              <CardContent className="flex-grow flex flex-col p-6">
                <h3 className="font-display text-xl font-semibold mb-1 text-chalk">
                  {member.name}
                </h3>
                <p className="text-white text-sm mb-3 font-medium">{member.role}</p>
                <p className="text-chalk/90 text-sm mb-4 flex-grow leading-relaxed">{member.bio}</p>
                {member.id === 'gabrielle' && (
                  <a
                    href={siteContent.press.voyageArticle}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-white hover:text-white/80 text-sm font-semibold transition-colors mt-auto"
                  >
                    Read full story on VoyageKC
                    <HiExternalLink className="h-4 w-4" />
                  </a>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* Credentials */}
      <section className="py-12 md:py-16 lg:py-20 checkered-bg relative">
        <div className="absolute inset-0 bg-dusty-rose/50 z-0" />
        <div className="container-custom relative z-10">
          <SectionHeader
            title="Credentials"
            subtitle="Professional qualifications you can trust"
            dark={true}
          />
          <div className="max-w-5xl mx-auto">
            <Card className="p-8 bg-black/20 border-white/10">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-y-8 md:gap-0 md:divide-x md:divide-dusty-rose/30 mb-8">
                <div className="flex flex-col px-4 md:px-8 md:col-span-1">
                  <h4 className="font-semibold mb-4 text-white text-center">Licensed In</h4>
                  <ul className="text-white/80 space-y-2 text-sm text-left">
                    {siteContent.credentials.licenses.map((license) => (
                      <li key={license} className="flex items-start gap-2">
                        <span className="text-white font-bold">•</span>
                        <span>{license}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex flex-col px-4 md:px-8 md:col-span-2">
                  <h4 className="font-semibold mb-4 text-white text-center">Awards</h4>
                  <ul className="text-white/80 space-y-2 text-sm text-left">
                    {siteContent.credentials.awards.map((award) => (
                      <li key={award} className="flex items-start gap-2">
                        <span className="text-white font-bold">•</span>
                        <span>{award}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex flex-col px-4 md:px-8 md:col-span-1">
                  <h4 className="font-semibold mb-4 text-white text-center">Insurance</h4>
                  <ul className="text-white/80 space-y-2 text-sm text-left">
                    <li className="flex items-start gap-2">
                      <span className="text-white font-bold">•</span>
                      <span>Fully Insured</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="text-center text-white italic font-medium mb-4">
                {siteContent.credentials.experience}
              </div>
              <div className="text-center">
                <a
                  href={siteContent.press.voyageArticle}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-white/80 font-semibold inline-flex items-center gap-2 underline underline-offset-4"
                >
                  Featured in VoyageKC Magazine
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <ChalkboardBackground className="py-16 md:py-24">
        <div className="container-custom">
          <SectionHeader
            title="Our Philosophy"
            dark={true}
          />
          <div className="flex flex-wrap justify-center gap-6 max-w-5xl mx-auto">
            {siteContent.about.philosophy.map((item, index) => {
              const Icon = philosophyIcons[index] || HiSparkles;
              return (
                <div key={index} className="text-center p-6 flex flex-col items-center w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-1.5rem)]">
                  <div className="inline-flex items-center justify-center w-12 h-12 border border-dusty-rose/30 rounded-full mb-4">
                    <Icon className="h-6 w-6 text-dusty-rose" />
                  </div>
                  <h3 className="font-semibold text-chalk uppercase tracking-wide">{item.title}</h3>
                </div>
              );
            })}
          </div>
        </div>
      </ChalkboardBackground>

      {/* CTA */}
      <ChalkboardBackground 
        as="section" 
        backgroundImage={homeBg}
        backgroundOpacity={0.5}
        className="py-12 md:py-16 lg:py-20"
      >
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-headline font-semibold mb-6 text-chalk uppercase tracking-wider">
              Let's Create Your Dream Look
            </h2>
            <Button
              href={siteContent.social.linktree}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline"
              size="lg"
              className="!text-xs"
            >
              Check Availability
              <HiExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </ChalkboardBackground>
    </>
  );
}
